import { useEffect, useState } from "react";
import { CrudPanel } from "../components/CrudPanel";
import { DateField } from "../components/DateField";
import { PhotoUploader, usePhotoUploader } from "../components/PhotoUploader";
import { propertyService } from "../services/propertyService";
import { tenantService } from "../services/tenantService";
import { seasonService } from "../services/seasonService";
import { pricingRuleService } from "../services/pricingRuleService";
import { Building, Plus, Trash2 } from "lucide-react";
import { emptyCalendar, parseWeekendDays, normalizeHolidays } from "../utils/rateTiers";
import { DAY_NAMES, today } from "../utils/date";
import { photoSlots, withPhotos } from "../utils/photos";

const emptyProperty = {
  tenantId: null,
  countryId: 1,
  stateId: "",
  cityId: "",
  name: "",
  contactPerson: "",
  address: "",
  navigationLink: "",
  status: "ACTIVE",
};

function photoStateForRow(row) {
  const photos = photoSlots(row);
  const requested = Number(row?.thumbnailPhoto || 0) - 1;
  return {
    photos,
    thumbnailIndex: photos.length ? Math.min(Math.max(requested, 0), photos.length - 1) : 0,
  };
}

function calendarFromEntry(entry) {
  return {
    weekendDays: Array.isArray(entry?.weekendDays) ? parseWeekendDays(entry.weekendDays) : null,
    holidays: normalizeHolidays(entry?.holidays),
    peakRanges: Array.isArray(entry?.peakRanges) ? entry.peakRanges : [],
  };
}

export function Property({ notify, setHeaderAction, tenantId }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ ...emptyProperty, tenantId });
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [allCities, setAllCities] = useState([]);
  const [tenantOptions, setTenantOptions] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [calendar, setCalendar] = useState(emptyCalendar());
  const [calendarDirty, setCalendarDirty] = useState(false);
  const [savingCalendar, setSavingCalendar] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const photos = usePhotoUploader({ notify });
  const baseFormFields = ["name", "contactPerson", "address", "stateId", "cityId", "status", "navigationLink", "isDeleted"];
  const isSuperAdmin = tenantId === null;
  const formFields = isSuperAdmin ? ["tenantId", ...baseFormFields] : baseFormFields;
  const [effectiveTenantId, setEffectiveTenantId] = useState(tenantId);

  useEffect(() => {
    if (setHeaderAction) {
      setHeaderAction(
        <button
          className={showForm ? "secondary" : "primary"}
          onClick={() => setShowForm(!showForm)}
          style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
        >
          {showForm ? "Hide" : "Add"} Property
        </button>
      );
    }
    return () => { if (setHeaderAction) setHeaderAction(null); };
  }, [showForm, setHeaderAction]);

  const loadStates = async (countryId) => {
    try {
      const data = await propertyService.listStates(countryId);
      setStates(data);
      setForm((prev) => ({ ...prev, stateId: "", cityId: "" }));
      if (data.length > 0) {
        setForm((prev) => ({ ...prev, stateId: data[0].id }));
        loadCities(data[0].id);
      } else {
        setCities([]);
      }
    } catch (err) {
      notify(err?.message || "Failed to load states.");
    }
  };

  const loadCities = async (stateId) => {
    try {
      const data = await propertyService.listCities(stateId);
      setCities(data);
      setForm((prev) => ({ ...prev, cityId: data.length > 0 ? data[0].id : "" }));
    } catch (err) {
      notify(err?.message || "Failed to load cities.");
    }
  };

  const loadAllCities = async () => {
    try {
      const data = await propertyService.listAllCities();
      setAllCities(data);
    } catch (err) {
      notify(err?.message || "Failed to load cities.");
    }
  };

  useEffect(() => {
    setForm((prev) => ({ ...prev, countryId: 1 }));
    loadStates(1);
    loadAllCities();
  }, []);

  useEffect(() => {
    if (isSuperAdmin) {
      tenantService.list()
        .then((list) => setTenantOptions(list.map((t) => ({ value: t.id, label: t.tenantName || `Tenant ${t.uuid.slice(0, 8)}` }))))
        .catch(() => setTenantOptions([]));
    }
  }, [isSuperAdmin]);

  const load = async () => {
    setLoading(true);
    try {
      const result = await propertyService.listForTenant(isSuperAdmin ? null : (effectiveTenantId === null ? undefined : effectiveTenantId));
      setRows(result);
    } catch (err) {
      notify(err?.message || "Failed to load properties.");
    } finally {
      setLoading(false);
    }
  };



  useEffect(() => { load(); }, [tenantId]);



  const hydrateCalendar = async (propertyName) => {
    if (!propertyName) {
      setCalendar(emptyCalendar());
      setCalendarDirty(false);
      return;
    }
    try {
      let prop = rows.find((r) => r.name === propertyName);
      let pid = prop?.id;
      if (!pid) {
        const all = await propertyService.listForTenant(tenantId);
        prop = all.find((r) => r.name === propertyName);
        pid = prop?.id;
      }
      if (pid) {
        const rulesRes = await pricingRuleService.list(pid);
        const rules = rulesRes?.content || [];

        const holidays = [];
        const peakRanges = [];
        const weekendDays = [];
        const dayMap = { "Weekend Sunday": 0, "Weekend Monday": 1, "Weekend Tuesday": 2, "Weekend Wednesday": 3, "Weekend Thursday": 4, "Weekend Friday": 5, "Weekend Saturday": 6 };
        for (const rule of rules) {
          try {
            const datesRes = await pricingRuleService.listDates(rule.id, { isActive: true });
            const dates = datesRes?.content || [];
            for (const d of dates) {
              if (rule.ruleType === "WEEKEND" && d.dateName) {
                const key = d.dateName.toLowerCase();
                if (key.includes("sun") || key.includes("sunday")) weekendDays.push({day:0,id:d.id,pricingRuleId:rule.id});
                else if (key.includes("mon") || key.includes("monday")) weekendDays.push({day:1,id:d.id,pricingRuleId:rule.id});
                else if (key.includes("tue") || key.includes("tuesday")) weekendDays.push({day:2,id:d.id,pricingRuleId:rule.id});
                else if (key.includes("wed") || key.includes("wednesday")) weekendDays.push({day:3,id:d.id,pricingRuleId:rule.id});
                else if (key.includes("thu") || key.includes("thursday")) weekendDays.push({day:4,id:d.id,pricingRuleId:rule.id});
                else if (key.includes("fri") || key.includes("friday")) weekendDays.push({day:5,id:d.id,pricingRuleId:rule.id});
                else if (key.includes("sat") || key.includes("saturday")) weekendDays.push({day:6,id:d.id,pricingRuleId:rule.id});
                else if (dayMap[d.dateName] !== undefined) weekendDays.push({day: dayMap[d.dateName], id: d.id, pricingRuleId: rule.id});
              }
              if (rule.ruleType === "HOLIDAY") {
                holidays.push({ id: d.id, pricingRuleId: rule.id, date: d.startDate, label: d.dateName || rule.ruleName });
              }
              if (rule.ruleType === "PEAK" || rule.ruleType === "SEASONAL") {
                peakRanges.push({ id: d.id, pricingRuleId: rule.id, start: d.startDate, end: d.endDate, label: d.dateName || rule.ruleName });
              }
            }
          } catch (e) {}
        }
        const weekendDaysDeduped = weekendDays.reduce((acc, w) => {
          const day = w && typeof w === "object" ? w.day : w;
          if (day === undefined || day === null) return acc;
          if (!acc.some(x => (x && typeof x === "object" ? x.day : x) === day)) {
            acc.push(w && typeof w === "object" ? w : { day });
          }
          return acc;
        }, []);
        weekendDaysDeduped.sort((a,b) => {
          const x = a && typeof a === "object" ? a.day : a;
          const y = b && typeof b === "object" ? b.day : b;
          return x - y;
        });
        setCalendar({ weekendDays: weekendDaysDeduped, holidays: normalizeHolidays(holidays), peakRanges });
        setCalendarDirty(false);
        return;
      }
      const { calendar: all } = await seasonService.load();
      setCalendar(calendarFromEntry(all?.[propertyName]));
      setCalendarDirty(false);
    } catch {
      setCalendar(emptyCalendar());
    }
  };

  async function handleEdit(row) {
    photos.clearPhotos();
    if (!row?.uuid) return;
    let detail = null;
    try {
      detail = await propertyService.get(row.uuid);
      photos.setPhotosFromRow(photoStateForRow(detail));
    } catch (err) {
      notify(err?.message || "Could not load photos for this property.");
    }
    // Load cities for the property's state so the city dropdown is populated on edit
    const stateIdForCities = detail?.stateId || row?.stateId;
    const cityIdForEdit = detail?.cityId || row?.cityId;
    if (stateIdForCities) {
      try {
        const cityData = await propertyService.listCities(stateIdForCities);
        setCities(cityData);
      } catch (err) {
        notify(err?.message || "Failed to load cities.");
      }
    }
    // Set form stateId and cityId from the property data
    setForm((prev) => ({
      ...prev,
      stateId: stateIdForCities || prev.stateId,
      cityId: cityIdForEdit || prev.cityId,
    }));
    try {
      const all = await propertyService.listForTenant(tenantId);
      setRows(all);
      const prop = all.find((r) => r.name === (row?.name || detail?.name) || r.uuid === row?.uuid);
      const pid = prop?.id || row?.id || detail?.id;
      const weekendDays = [];
      const holidays = [];
      const peakRanges = [];
      if (pid) {
        const rulesRes = await pricingRuleService.list(pid);
        const rules = rulesRes?.content || [];
        for (const rule of rules) {
          if (rule.ruleType === "WEEKEND") {
            const datesRes = await pricingRuleService.listDates(rule.id, { isActive: true });
            const dates = datesRes?.content || [];
            for (const d of dates) {
              const key = String(d.dateName || "").toLowerCase();
              if (key.includes("sun")) weekendDays.push({day:0,id:d.id,pricingRuleId:rule.id});
              if (key.includes("mon")) weekendDays.push({day:1,id:d.id,pricingRuleId:rule.id});
              if (key.includes("tue")) weekendDays.push({day:2,id:d.id,pricingRuleId:rule.id});
              if (key.includes("wed")) weekendDays.push({day:3,id:d.id,pricingRuleId:rule.id});
              if (key.includes("thu")) weekendDays.push({day:4,id:d.id,pricingRuleId:rule.id});
              if (key.includes("fri")) weekendDays.push({day:5,id:d.id,pricingRuleId:rule.id});
              if (key.includes("sat")) weekendDays.push({day:6,id:d.id,pricingRuleId:rule.id});
            }
          } else if (rule.ruleType === "HOLIDAY") {
            const datesRes = await pricingRuleService.listDates(rule.id, { isActive: true });
            const dates = datesRes?.content || [];
            for (const d of dates) {
              const start = d.startDate || d.date || "";
              const label = d.dateName || d.name || "";
              if (start && label) {
                holidays.push({ id: d.id, pricingRuleId: rule.id, date: (start+"").slice(0,10), label });
              }
            }
          } else if (rule.ruleType === "PEAK" || rule.ruleType === "SEASONAL") {
            const datesRes = await pricingRuleService.listDates(rule.id, { isActive: true });
            const dates = datesRes?.content || [];
            for (const d of dates) {
              const start = String(d.startDate || "").slice(0, 10);
              const end = String(d.endDate || "").slice(0, 10);
              const label = d.dateName || d.name || rule.ruleName || "";
              if (start && end && label) {
                peakRanges.push({ start, end, label });
              }
            }
          }
        }
      }
      setCalendar({ weekendDays: [...new Set(weekendDays)].sort((a,b)=>a-b), holidays: normalizeHolidays(holidays), peakRanges });
      setCalendarDirty(false);
    } catch (e) {}
  }

  function handleFieldChange(key, value) {
    if (key === "name") hydrateCalendar(value);
    if (key === "tenantId") {
      setEffectiveTenantId(value);
      load();
    }
    if (key === "stateId") {
      setForm((prev) => ({ ...prev, cityId: "" }));
      if (value) loadCities(value);
      else setCities([]);
    }
  }

  function updateCalendar(mutator) {
    setCalendar((current) => mutator({ ...current }));
    setCalendarDirty(true);
  }

  function updateHoliday(index, patch) {
    updateCalendar((c) => {
      const holidays = [...c.holidays];
      holidays[index] = { date: "", label: "", ...holidays[index], ...patch };
      return { ...c, holidays };
    });
  }

  function weekendDayIndices(weekendDays) {
    return (weekendDays || [])
      .map((d) => (d && typeof d === "object" ? d.day : d))
      .filter((d) => Number.isInteger(d) && d >= 0 && d <= 6);
  }

  function sameLabel(a, b) {
    return String(a || "").trim().toLowerCase() === String(b || "").trim().toLowerCase();
  }

  async function persistCalendarToSettings(propertyName, state) {
    if (!propertyName) return;
    const weekendDays = weekendDayIndices(state?.weekendDays);
    const holidays = normalizeHolidays(state?.holidays).map(({ date, label }) => ({ date, label }));
    const peakRanges = (state?.peakRanges || [])
      .filter((range) => range && range.start && range.end)
      .map(({ start, end, label }) => ({ start: String(start).slice(0, 10), end: String(end).slice(0, 10), label }));
    try {
      await seasonService.savePropertyCalendar(propertyName, { weekendDays, holidays, peakRanges });
    } catch { /* settings sync is best-effort */ }
  }

  async function toggleWeekendDay(day) {
    const existing = (calendar.weekendDays || []).find((w) => (w && typeof w === 'object' ? w.day === day : w === day));
    if (existing && typeof existing === 'object' && existing.id && existing.pricingRuleId) {
      if (!window.confirm('Remove this weekend day?')) return;
      try {
        await pricingRuleService.removeDate(existing.id, existing.pricingRuleId);
        const remaining = (calendar.weekendDays || []).filter((d) => (d && typeof d === 'object' ? d.day !== day : d !== day));
        await persistCalendarToSettings(form.name, { ...calendar, weekendDays: remaining });
        await hydrateCalendar(form.name);
      } catch (err) {
        notify(err?.message || 'Could not remove weekend day.');
      }
      return;
    }
    updateCalendar((c) => {
      const current = c.weekendDays || [];
      const has = current.some((d) => (d && typeof d === 'object' ? d.day === day : d === day));
      const next = has
        ? current.filter((d) => (d && typeof d === 'object' ? d.day !== day : d !== day))
        : [...current, { day }].sort((a, b) => { const x = a && typeof a === 'object' ? a.day : a; const y = b && typeof b === 'object' ? b.day : b; return x - y; });
      return { ...c, weekendDays: next };
    });
  }



  async function saveCalendarInternal() {
    const propertyName = form.name;
    if (!propertyName) return;
    const peakRanges = calendar.peakRanges
      .filter((range) => range.start && range.end)
      .map(({ start, end, label }) => ({ start: String(start).slice(0, 10), end: String(end).slice(0, 10), label }))
      .sort((a, b) => (a.start < b.start ? -1 : 1));
    const holidays = normalizeHolidays(calendar.holidays);
    const weekendDays = weekendDayIndices(calendar.weekendDays);
    await seasonService.savePropertyCalendar(propertyName, { weekendDays, holidays, peakRanges });
    setCalendar({ weekendDays, holidays, peakRanges });
    setCalendarDirty(false);
  }

  async function save(event) {
    event.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      const saved = await propertyService.save(withPhotos({ ...form, uuid: form.uuid || null, tenantId: form.tenantId, countryId: form.countryId || 1 }, photos.photos, photos.thumbnailIndex));
      const savedName = form.name;
      const savedPropId = saved?.id;
      setForm({ ...emptyProperty, tenantId: effectiveTenantId });
      setShowForm(false);
      photos.clearPhotos();
      notify("Property saved.");
      if (calendarDirty || form.name) {
        try { await saveCalendarInternal(); } catch (e) { console.error("Property calendar save error:", e); notify(e?.message || "Property saved, but calendar settings could not be saved."); }
      }
      try {
        const savedProp = savedPropId ? { id: savedPropId } : null;
        if (savedProp?.id) {
          let weekendRule = (await pricingRuleService.list(savedProp.id, { isActive: true }))?.content?.find(r => r.ruleType === "WEEKEND");
          if (!weekendRule) {
            weekendRule = await pricingRuleService.create({ propertyId: savedProp.id, ruleName: "Weekend", ruleType: "WEEKEND", priority: 5, isEnabled: true });
          }
          const dayNames = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];
          const wDatesRes = await pricingRuleService.listDates(weekendRule.id);
          const allWeekendDates = wDatesRes?.content || [];
          const wdays = calendar.weekendDays || [];
          const desiredWNames = wdays.map((d) => `Weekend ${dayNames[d && typeof d === "object" ? d.day : d]}`);
          const matchedWIds = new Set();
          for (const name of desiredWNames) {
            const matches = allWeekendDates.filter((x) => sameLabel(x.dateName, name));
            const existing = matches[0];
            if (existing) {
              matchedWIds.add(existing.id);
              if (!existing.isEnabled || String(existing.dateName || "") !== name) {
                await pricingRuleService.updateDate(
                  existing.id,
                  { pricingRuleId: weekendRule.id, dateName: name, startDate: existing.startDate, endDate: existing.endDate, isEnabled: true },
                  weekendRule.id
                );
              }
            } else {
              await pricingRuleService.createDate({ pricingRuleId: weekendRule.id, dateName: name, startDate: "1900-01-01", endDate: "2099-12-31", isEnabled: true });
            }
          }
          for (const aw of allWeekendDates) {
            if (!matchedWIds.has(aw.id)) {
              try { await pricingRuleService.removeDate(aw.id, weekendRule.id); } catch {}
            }
          }
          let holidayRule = (await pricingRuleService.list(savedProp.id, { isActive: true }))?.content?.find(r => r.ruleType === "HOLIDAY");
          if (!holidayRule) {
            holidayRule = await pricingRuleService.create({ propertyId: savedProp.id, ruleName: "Holiday", ruleType: "HOLIDAY", priority: 8, isEnabled: true });
          }
          const hDatesRes = await pricingRuleService.listDates(holidayRule.id);
          const allHolidayDates = hDatesRes?.content || [];
          const holidays = normalizeHolidays(calendar.holidays);
          const desiredHolidays = holidays.filter((h) => h.date && h.label);
          const matchedHIds = new Set();
          for (const h of desiredHolidays) {
            const matches = allHolidayDates.filter((x) => sameLabel(x.dateName, h.label));
            const existing = matches[0];
            if (existing) {
              matchedHIds.add(existing.id);
              const existingStart = String(existing.startDate || "").slice(0, 10);
              const existingEnd = String(existing.endDate || "").slice(0, 10);
              if (
                !existing.isEnabled ||
                String(existing.dateName || "") !== h.label ||
                existingStart !== h.date ||
                existingEnd !== h.date
              ) {
                await pricingRuleService.updateDate(
                  existing.id,
                  { pricingRuleId: holidayRule.id, dateName: h.label, startDate: h.date, endDate: h.date, isEnabled: true },
                  holidayRule.id
                );
              }
            } else {
              await pricingRuleService.createDate({ pricingRuleId: holidayRule.id, dateName: h.label, startDate: h.date, endDate: h.date, isEnabled: true });
            }
          }
          for (const eh of allHolidayDates) {
            if (!matchedHIds.has(eh.id)) {
              try { await pricingRuleService.removeDate(eh.id, holidayRule.id); } catch {}
            }
          }
          let peakRule = (await pricingRuleService.list(savedProp.id, { isActive: true }))?.content?.find(r => r.ruleType === "PEAK");
          if (!peakRule) {
            peakRule = await pricingRuleService.create({ propertyId: savedProp.id, ruleName: "Peak Season", ruleType: "PEAK", priority: 10, isEnabled: true });
          }
          const pDatesRes = await pricingRuleService.listDates(peakRule.id);
          const allPeakDates = pDatesRes?.content || [];
          const peaks = (calendar.peakRanges || [])
            .map((range) => ({
              start: String(range.start || "").slice(0, 10),
              end: String(range.end || "").slice(0, 10),
              label: String(range.label || "").trim(),
            }))
            .filter((range) => range.start && range.end && range.label && range.start <= range.end);
          const matchedPIds = new Set();
          for (const pr of peaks) {
            const matches = allPeakDates.filter((x) => sameLabel(x.dateName, pr.label));
            const existing = matches[0];
            if (existing) {
              matchedPIds.add(existing.id);
              const existingStart = String(existing.startDate || "").slice(0, 10);
              const existingEnd = String(existing.endDate || "").slice(0, 10);
              if (
                !existing.isEnabled ||
                String(existing.dateName || "") !== pr.label ||
                existingStart !== pr.start ||
                existingEnd !== pr.end
              ) {
                await pricingRuleService.updateDate(
                  existing.id,
                  { pricingRuleId: peakRule.id, dateName: pr.label, startDate: pr.start, endDate: pr.end, isEnabled: true },
                  peakRule.id
                );
              }
            } else {
              await pricingRuleService.createDate({ pricingRuleId: peakRule.id, dateName: pr.label, startDate: pr.start, endDate: pr.end, isEnabled: true });
            }
          }
          for (const ep of allPeakDates) {
            if (!matchedPIds.has(ep.id)) {
              try { await pricingRuleService.removeDate(ep.id, peakRule.id); } catch {}
            }
          }
        }
      } catch (err) {
        console.error("Property save error:", err);
        notify(err?.message || "Property pricing rules could not be saved. The property was saved, but weekend/holiday/peak pricing was not applied.");
      }
      load();
      hydrateCalendar("");
      if (savedName) {
        try {
          const { calendar: all } = await seasonService.load();
          setCalendar(calendarFromEntry(all?.[savedName]));
        } catch {
          setCalendar(emptyCalendar());
        }
      }

    } catch (err) {
      console.error("Property save error:", err);
      notify(err?.message || "Could not save property. Please check the details and try again.");
    } finally {
      setSaving(false);
    }
  }

  async function removeHoliday(index) {
    const h = calendar.holidays[index];
    if (h && typeof h === 'object' && h.id && h.pricingRuleId) {
      if (!window.confirm('Delete this holiday?')) return;
      try {
        await pricingRuleService.removeDate(h.id, h.pricingRuleId);
        const holidays = calendar.holidays.filter((_, i) => i !== index);
        await persistCalendarToSettings(form.name, { ...calendar, holidays });
        await hydrateCalendar(form.name);
      } catch (err) {
        notify(err?.message || 'Could not delete holiday.');
      }
      return;
    }
    updateCalendar((c) => ({ ...c, holidays: c.holidays.filter((_, i) => i !== index) }));
  }

  async function removePeakRange(index) {
    const r = calendar.peakRanges[index];
    if (r && typeof r === 'object' && r.id && r.pricingRuleId) {
      if (!window.confirm('Delete this peak season range?')) return;
      try {
        await pricingRuleService.removeDate(r.id, r.pricingRuleId);
        const peakRanges = calendar.peakRanges.filter((_, i) => i !== index);
        await persistCalendarToSettings(form.name, { ...calendar, peakRanges });
        await hydrateCalendar(form.name);
      } catch (err) {
        notify(err?.message || 'Could not delete peak range.');
      }
      return;
    }
    updateCalendar((c) => ({ ...c, peakRanges: c.peakRanges.filter((_, i) => i !== index) }));
  }
  async function remove(uuid) {
    if (!window.confirm("Delete this property?")) return;
    try {
      await propertyService.remove(uuid);
      notify("Property deleted.");
      load();
    } catch (err) {
      console.error("Property delete error:", err);
      notify(err?.message || "Could not delete property.");
    }
  }

  async function restore(uuid) {
    try {
      await propertyService.restore(uuid);
      notify("Property restored.");
      load();
    } catch (err) {
      console.error("Property restore error:", err);
      notify(err?.message || "Could not restore property.");
    }
  }

  return (
    <>
      <CrudPanel
        title="Property"
        sectionLabel={<span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}><Building size={18} />Property Management</span>}
        header="Property List"
        rows={rows}
        loading={loading}
        saving={saving}
        form={form}
        setForm={setForm}
        emptyForm={emptyProperty}
        save={save}
        remove={remove}
        fields={formFields}
        firstRowFields={["name", "contactPerson", "address"]}
        numberFields={[]}
        requiredFields={isSuperAdmin ? ["name", "stateId", "cityId", "tenantId"] : ["name", "stateId", "cityId"]}
        hideTableFields={["navigationLink"]}
        hideFormFields={["isDeleted"]}
        collapsible={true}
        selectFields={{
          ...(isSuperAdmin && { tenantId: tenantOptions.map((t) => ({ value: t.value, label: t.label })) }),
          stateId: states.map((s) => ({ value: s.id, label: s.name })),
          cityId: allCities.map((c) => ({ value: c.id, label: c.name })),
          status: ["ACTIVE", "INACTIVE"],
        }}
        formSelectFields={{
          ...(isSuperAdmin && { tenantId: tenantOptions.map((t) => ({ value: t.value, label: t.label })) }),
          stateId: states.map((s) => ({ value: s.id, label: s.name })),
          cityId: cities.map((c) => ({ value: c.id, label: c.name })),
          status: ["ACTIVE", "INACTIVE"],
        }}
        fieldLabels={{
          tenantId: "Tenant",
          stateId: "State",
          cityId: "City",
        }}
        columnLabels={{
          tenantId: "Tenant Name",
          cityId: "City Name",
          isDeleted: "Deleted",
        }}
        showForm={showForm}
        setShowForm={setShowForm}
        buttonLabel="Property"
        onEdit={handleEdit}
        onFieldChange={handleFieldChange}
        onRestore={restore}
        onClear={photos.clearPhotos}
        extraContent={
          <>
          <PhotoUploader label="Property Photos" {...photos} />
          <div className="image-upload-area seasonal-block">
            <p className="section-label">Seasonal Calendar</p>
            {!form.name ? (
              <p className="image-hint">Enter a property name above to configure its weekend days, holidays and peak season dates.</p>
            ) : (
              <>
                <div className="seasonal-sub">
                  <div className="seasonal-sub-head">
                    <strong>Weekend Days</strong>

                  </div>
                  <div className="weekday-toggles">
                    {DAY_NAMES.map((name, day) => {
                       const on = (calendar.weekendDays || []).some((w) => (w && typeof w === "object" ? w.day === day : w === day));
                      return (
                        <button
                          type="button"
                          key={name}
                          className={`weekday-toggle ${on ? "on" : ""}`}
                          onClick={() => toggleWeekendDay(day)}
                          title={on ? `Remove ${name}` : `Add ${name}`}
                        >
                          {name}
                        </button>
                      );
                    })}
                  </div>
                  <p className="image-hint">
                    Overrides the global weekend days from Settings for this property only.
                       {calendar.weekendDays && calendar.weekendDays.length
                         ? " Click any day again to remove it."
                          : " No weekend days for this property — weekend rates are disabled here."}
                  </p>
                </div>

                <div className="seasonal-sub">
                  <div className="seasonal-sub-head">
                    <strong>Holidays</strong>
                    <div className="seasonal-actions">

                      <button
                        type="button"
                        className="secondary"
                        onClick={() => updateCalendar((c) => ({ ...c, holidays: [...c.holidays, { date: "", label: "" }] }))}
                      >
                        <Plus size={14} />Add Holiday
                      </button>
                    </div>
                  </div>
                  {calendar.holidays.length === 0 ? (
                    <p className="image-hint">No holidays marked.</p>
                  ) : (
                    <div className="seasonal-rows">
                      {calendar.holidays.map((holiday, index) => (
                        <div className="seasonal-row" key={`${holiday.date || "new"}-${index}`}>
                          <DateField
                            value={holiday.date}
                            onChange={(next) => updateHoliday(index, { date: next })}
                            showWeekday
                            ariaLabel="Holiday date"
                          />
                          <input
                            type="text"
                            placeholder="Holiday name (e.g. Christmas)"
                            aria-label="Holiday name"
                            value={holiday.label || ""}
                            onChange={(e) => updateHoliday(index, { label: e.target.value })}
                          />
                          <button
                            type="button"
                            className="icon-btn"
                            onClick={() => updateCalendar((c) => ({ ...c, holidays: c.holidays.filter((_, i) => i !== index) }))}
                            title="Remove holiday"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="seasonal-sub">
                  <div className="seasonal-sub-head">
                    <strong>Peak Season Ranges</strong>
                    <button
                      type="button"
                      className="secondary"
                      onClick={() => updateCalendar((c) => ({ ...c, peakRanges: [...c.peakRanges, { start: "", end: "", label: "" }] }))}
                    >
                      <Plus size={14} />Add Range
                    </button>
                  </div>
                  {calendar.peakRanges.length === 0 ? (
                    <p className="image-hint">No peak season ranges defined.</p>
                  ) : (
                    <div className="seasonal-rows">
                      {calendar.peakRanges.map((range, index) => (
                        <div className="seasonal-row peak" key={index}>
                          <DateField
                            value={range.start}
                            onChange={(next) => updateCalendar((c) => {
                              const peakRanges = [...c.peakRanges];
                              peakRanges[index] = { ...range, start: next };
                              return { ...c, peakRanges };
                            })}
                            showWeekday
                            ariaLabel="Peak season start date"
                          />
                          <DateField
                            value={range.end}
                            onChange={(next) => updateCalendar((c) => {
                              const peakRanges = [...c.peakRanges];
                              peakRanges[index] = { ...range, end: next };
                              return { ...c, peakRanges };
                            })}
                            showWeekday
                            ariaLabel="Peak season end date"
                          />
                          <input
                            type="text"
                            placeholder="Label (e.g. New Year)"
                            value={range.label || ""}
                            onChange={(e) => updateCalendar((c) => {
                              const peakRanges = [...c.peakRanges];
                              peakRanges[index] = { ...range, label: e.target.value };
                              return { ...c, peakRanges };
                            })}
                          />
                          <button
                            type="button"
                            className="icon-btn"
                            onClick={() => updateCalendar((c) => ({ ...c, peakRanges: c.peakRanges.filter((_, i) => i !== index) }))}
                            title="Remove range"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>




              </>
            )}
          </div>
          </>
        }
      />
    </>
  );
}







